import axios from 'axios'
import { ref, isRef, unref, computed, watch } from 'vue'

/**
 * Makes a request via axios to a given url whenever it changes
 * 
 * @param {string} url
 * @param {string=get|object} method
 * @returns 
 */
export default function useAxios(url, method = 'get') {
    const data = ref({})
    const error = ref(null)
    const loading = ref(false)
    const cancellationController = ref(null)

    /**
     * So we can pass other options or just a method
     */
    const config = computed(() => {
        return typeof method === 'object'
            ? method
            : {
                method    : method,
                immediate : true
            }
    })

    function makeRequest() {
        // reset state before fetching
        loading.value = true

        // We need a new abort controller per request because new requests wouldn't be sent
        // if we kept an instance which had been aborted
        cancellationController.value = new AbortController()

        if(config.value.resetOnLoad ?? false){
            data.value = {}
            error.value = null
        }

        // unref() unwraps potential refs
        axios({
            method : config.value?.method ?? 'get',
            url    : unref(url),
            signal : cancellationController.value.signal
        })
            .then((response) => {
                loading.value = false
                data.value = config.value?.mapResponse
                    ? config.value?.mapResponse(response.data)
                    : response.data
            })
            .catch((error) => {
                loading.value = false
                error.value = error
            })
    }

    //if the url is a ref we want to watchEffect so we can automatically make a request whenever url is changed
    if (isRef(url)) {
        watch(url, makeRequest) //if the url changes we should debounce requests
    }

    //If we're to make a request on load do it here as we've already initialized everything
    if(config.value.immediate){
        makeRequest()
    }

    return { 
        data,
        error,
        loading,
        cancellationController
    }
}